<template>
  <div class="comments-card">
    <div v-if="loading" class="loading-state">Loading comments...</div>
    <div v-else-if="error" class="error-state">
      {{ error }}
    </div>
    <div v-else class="comments-list">
      <div v-for="comment in comments" :key="comment.id" class="comment-item">
        <div class="comment-content" @click="startEditing(comment)">
          <p v-if="!isEditing(comment)">{{ comment.content }}</p>
          <div v-else class="edit-form">
            <Textarea
              v-model="editingContent"
              class="edit-textarea"
              :autoResize="true"
              rows="1"
              @keydown.enter.prevent="saveEdit"
              @keydown.esc="cancelEdit"
            />
            <div class="edit-actions">
              <Button @click.stop="saveEdit" severity="success" size="small">Save</Button>
              <Button @click.stop="cancelEdit" severity="secondary" size="small">Cancel</Button>
            </div>
          </div>
          <small class="comment-meta">
            {{
              comment.createdAt ? CommentModel.formatDate(new Date(comment.createdAt)) : 'recently'
            }}
            <span v-if="CommentModel.isEdited(comment)">(edited)</span>
          </small>
        </div>
      </div>
    </div>

    <div class="new-comment mt-5">
      <Textarea
        v-model="newComment"
        class="new-comment-input"
        placeholder="Add a comment..."
        :autoResize="true"
        rows="1"
        @keydown.enter.prevent="addComment"
      />
      <Button
        @click="addComment"
        :disabled="!newComment.trim()"
        severity="primary"
        class="add-comment-btn"
      >
        Add Comment
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect, watch } from 'vue'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import { useCommentStore, type Comment } from '@/stores/commentStore'
import { CommentModel } from '@/models/CommentModel'
import { useCommentService } from '@/services/commentService'
import { useUserStore } from '@/stores/user'

const props = defineProps<{
  organizationId?: string
  personId?: string
  taskId?: string
  transactionId?: string
}>()

const emit = defineEmits(['comment-added', 'comment-updated', 'comment-deleted'])

// Services and stores for data management
const commentStore = useCommentStore()
const commentService = useCommentService()
const userStore = useUserStore()

// Local state
const newComment = ref('')
const editingComment = ref<Comment | null>(null)
const editingContent = ref('')

// Use computed properties to reference store state
const loading = computed(() => commentStore.loading)
const error = computed(() => commentStore.error)
const comments = computed(() => {
  if (props.organizationId) {
    return commentStore.items.filter((c: Comment) => c.organizationId === props.organizationId)
  } else if (props.personId) {
    return commentStore.items.filter((c: Comment) => c.personId === props.personId)
  } else if (props.taskId) {
    return commentStore.items.filter((c: Comment) => c.taskId === props.taskId)
  } else if (props.transactionId) {
    return commentStore.items.filter((c: Comment) => c.transactionId === props.transactionId)
  }
  return []
})

const isEditing = (comment: Comment) => editingComment.value?.id === comment.id

const startEditing = (comment: Comment) => {
  editingComment.value = comment
  editingContent.value = comment.content
}

const cancelEdit = () => {
  editingComment.value = null
  editingContent.value = ''
}

const saveEdit = async () => {
  if (editingComment.value && editingContent.value.trim()) {
    try {
      // Update the comment with new content
      const updatedComment: Comment = {
        ...editingComment.value,
        content: editingContent.value.trim(),
        updatedAt: new Date(),
      }

      // Validate before updating
      if (CommentModel.validate(updatedComment)) {
        await commentStore.update(updatedComment.id, updatedComment)
        emit('comment-updated', updatedComment)
        cancelEdit()
      } else {
        console.error('Invalid comment data')
      }
    } catch (err) {
      console.error('Error updating comment:', err)
    }
  }
}

const addComment = async () => {
  if (newComment.value.trim()) {
    try {
      // Create the comment data using the model
      const commentData: Partial<Comment> = {
        content: newComment.value.trim(),
        organizationId: props.organizationId,
        personId: props.personId,
        taskId: props.taskId,
        transactionId: props.transactionId,
        createdBy: userStore.user?.uid || 'unknown',
      }

      // Create a comment using the service instead of directly using the store
      const comment = await commentService.addComment(commentData)
      emit('comment-added', comment)
      newComment.value = ''
    } catch (err) {
      console.error('Error adding comment:', err)
    }
  }
}

// Load comments when component mounts or props change
watchEffect(async () => {
  try {
    if (props.organizationId) {
      await commentService.getCommentsForOrganization(props.organizationId)
    } else if (props.personId) {
      await commentService.getCommentsForPerson(props.personId)
    } else if (props.taskId) {
      await commentService.getCommentsForTask(props.taskId)
    }
  } catch (err) {
    console.error('Error loading comments:', err)
  }
})

// Watch for changes in the comment store to update the comments list
watch(
  () => commentStore.items,
  () => {
    // This will trigger a re-computation of the comments computed property
  },
  { deep: true },
)
</script>

<style scoped>
.comments-card {
  @apply bg-white rounded-lg shadow p-4;
}

.loading-state,
.error-state {
  @apply text-center py-4 text-gray-500;
}

.error-state {
  @apply text-red-500;
}

.comments-list {
  @apply space-y-4 mb-4;
}

.comment-item {
  @apply border-b border-gray-200 pb-4 last:border-b-0 last:pb-0;
}

.comment-content {
  @apply cursor-pointer hover:bg-gray-50 p-2 rounded;
}

.comment-meta {
  @apply text-gray-500 text-sm mt-1 block;
}

.edit-form {
  @apply space-y-2;
}

.edit-textarea {
  @apply w-full;
}

.edit-actions {
  @apply flex space-x-2;
}

.new-comment {
  @apply mt-4 space-y-2;
}

.new-comment-input {
  @apply w-full;
}

.add-comment-btn {
  @apply mt-2;
}
</style>
